import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { CurriculumItem } from '@/data/classes';

interface CurriculumSidebarProps {
    classId: number;
    curriculum: CurriculumItem[];
    activeSessionId: string | null;
    basePath: string;
    isManagerPath: boolean;
}

/** 헤더 «총 N분» — durationMinutes 우선 (ClassDetail과 동일) */
function totalMinutesForCurriculum(items: CurriculumItem[]): number {
    const fromNumeric = items.reduce((acc, i) => acc + (i.durationMinutes ?? 0), 0);
    if (fromNumeric > 0) return fromNumeric;
    return items.reduce((acc, item) => {
        const match = item.duration?.match(/(\d+)/);
        return match ? acc + parseInt(match[1], 10) : acc;
    }, 0);
}

function isPlaceholderDuration(d: string | undefined): boolean {
    if (d == null) return true;
    const x = d.trim();
    return x === '' || x === '-' || x === '—';
}

/** 두 번째 줄: 소요시간(동영상이고 정보 없으면 숨김) */
function curriculumTimeLine(
    item: CurriculumItem,
    t: (key: string, opts?: Record<string, string | number>) => string
): string | null {
    if (item.durationMinutes != null && item.durationMinutes > 0) {
        return `${item.durationMinutes}${t('detail.minutes')}`;
    }
    if (!isPlaceholderDuration(item.duration)) {
        return item.duration;
    }
    if (item.type === 'video') return null;
    return null;
}

export default function CurriculumSidebar({
    classId,
    curriculum,
    activeSessionId,
    basePath,
    isManagerPath
}: CurriculumSidebarProps): React.ReactElement {
    const { t } = useTranslation('class');
    const navigate = useNavigate();

    const totalMin = useMemo(() => totalMinutesForCurriculum(curriculum), [curriculum]);

    const handleSessionClick = (item: CurriculumItem) => {
        const sessionPath = isManagerPath
            ? `${basePath}/class-management/${classId}/curriculum/${item.id}`
            : `/class/${classId}/curriculum/${item.id}`;
        navigate(sessionPath);
    };

    return (
        <div className="w-64 bg-white border-r border-gray-200 flex flex-col h-full flex-shrink-0">
            {/* List Header */}
            <div className="p-4 border-b border-gray-100 bg-white flex items-center justify-between gap-2">
                <h3 className="font-semibold text-gray-700 shrink-0">{t('shared.curriculum')}</h3>
                <span className="text-xs font-medium text-blue-500 text-right leading-tight">
                    {t('detail.sessionsCountTotal', { count: curriculum.length })}
                    <span className="text-gray-500 font-normal">
                        {' · '}
                        {totalMin}
                        {t('detail.minutes')}
                    </span>
                </span>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto">
                <div className="divide-y divide-gray-100">
                    {curriculum.map((item) => {
                        const isActive = activeSessionId === item.id.toString();
                        const timeLine = curriculumTimeLine(item, t);
                        return (
                            <button
                                key={item.id}
                                onClick={() => handleSessionClick(item)}
                                className={`w-full text-left p-4 hover:bg-gray-50 transition-colors relative group ${isActive ? 'bg-blue-50 hover:bg-blue-50' : ''
                                    }`}
                            >
                                {isActive && (
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-600" />
                                )}
                                <div className="flex items-start gap-3">
                                    <div className={`mt-0.5 ${isActive ? 'text-blue-600' : 'text-gray-400'}`}>
                                        <span className={`inline-block w-12 text-center text-xs font-medium px-1.5 py-0.5 rounded border ${item.type === 'video'
                                            ? 'bg-purple-50 text-purple-700 border-purple-200'
                                            : 'bg-blue-50 text-blue-700 border-blue-200'
                                            }`}>
                                            {item.type === 'video' ? t('detail.video') : t('detail.simulation')}
                                        </span>
                                    </div>
                                    <div>
                                        <h4 className={`text-sm font-medium line-clamp-2 ${isActive ? 'text-blue-700' : 'text-gray-700'
                                            }`}>
                                            {item.name}
                                        </h4>
                                        {item.practiceMode && (
                                            <span className="text-xs text-gray-500 block mt-0.5">{item.practiceMode}</span>
                                        )}
                                        {timeLine != null && (
                                            <span className="text-xs text-gray-500 tabular-nums">{timeLine}</span>
                                        )}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
